import { Subscription } from '../rpc'

export type MediaPlaybackStatus = 'playing' | 'paused' | 'buffering'
export type MediaPlaybackAction = 'play' | 'pause'

export interface MediaPlaybackState {
  status: MediaPlaybackStatus
  positionSeconds: number
  durationSeconds: number | null
}

export interface MediaPlaybackActivityIntent {
  kind: 'mediaPlayback'
  key: string
  contentId: string
  title: string
  state: MediaPlaybackState
  supportedActions: MediaPlaybackAction[]
  continuationParams?: Record<string, string>
}

export type ActivityIntent = MediaPlaybackActivityIntent
export type ActivityState = MediaPlaybackState

export interface StartActivityResult {
  activityId: string
  status: 'accepted' | 'skipped'
  reason?: 'unsupported_surface'
}

export interface UpdateActivityInput {
  activityId: string
  state: ActivityState
}

export interface EndActivityInput {
  activityId: string
  finalState?: ActivityState
}

export interface ActivityActionEvent {
  activityId: string
  action: MediaPlaybackAction
}

/**
 * Logical activity dispatch. The game describes what it is doing; the host
 * decides which native surfaces (Fire TV MediaSession today; iOS Now Playing,
 * Live Activities, Android Live Updates later) present it. Games never name a
 * surface.
 */
export interface ActivityApi {
  startActivityAsync(intent: ActivityIntent): Promise<StartActivityResult>
  updateActivity(input: UpdateActivityInput): void
  endActivityAsync(input: EndActivityInput): Promise<void>
  onActivityAction(callback: (event: ActivityActionEvent) => void): Subscription
}

const MEDIA_PLAYBACK_ACTIONS: readonly MediaPlaybackAction[] = ['play', 'pause']

export function isMediaPlaybackAction(value: unknown): value is MediaPlaybackAction {
  return (
    typeof value === 'string' &&
    MEDIA_PLAYBACK_ACTIONS.includes(value as MediaPlaybackAction)
  )
}

export function isActivityActionEvent(value: unknown): value is ActivityActionEvent {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<ActivityActionEvent>
  if (typeof candidate.activityId !== 'string') return false
  if (candidate.activityId.trim().length === 0) return false
  return isMediaPlaybackAction(candidate.action)
}
